<template>
	<div class="team-filter-section">
		<!-- Filters Section -->
		<div class="filter-header">
			<!-- Search Bar -->
			<div class="search-box-wrapper">
				<div class="search-box">
					<i class="bi bi-search"></i>
					<input
						v-model="searchQuery"
						type="text"
						class="form-control"
						placeholder="Search by name..."
						@input="debouncedSearch"
					/>
					<button v-if="searchQuery" @click="clearSearch" class="clear-search">
						<i class="bi bi-x-circle"></i>
					</button>
				</div>
			</div>

			<!-- Location Filter (Dropdown) -->
			<div class="filter-select">
				<select v-model="selectedLocation" class="form-select">
					<option value="">All Locations</option>
					<option v-for="location in locations" :key="location.id" :value="location.id">
						{{ location.title }}
					</option>
				</select>
			</div>

			<!-- Department Filter (Dropdown) -->
			<div class="filter-select">
				<select v-model="selectedDepartment" class="form-select">
					<option value="">All Departments</option>
					<option v-for="dept in departments" :key="dept.slug" :value="dept.slug">
						{{ dept.name }}
					</option>
				</select>
			</div>
		</div>

		<!-- Results Info -->
		<div class="results-info">
			<p>Showing {{ filteredTeam.length }} of {{ team.length }} team members</p>
		</div>

		<!-- Loading State -->
		<div v-if="loading" class="loading-container">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>

		<!-- Error State -->
		<div v-else-if="error" class="error-container">
			<div class="alert alert-danger">
				<i class="bi bi-exclamation-triangle"></i>
				{{ error }}
			</div>
		</div>

		<!-- Team Grid -->
		<div v-else class="team-grid">
			<div v-if="filteredTeam.length === 0" class="no-results">
				<i class="bi bi-people"></i>
				<p>No team members found matching your criteria.</p>
			</div>
			<div v-for="member in filteredTeam" :key="member.id" class="team-card">
				<div class="team-info">
					<h3 class="team-name">{{ member.name }}</h3>
					<p v-if="member.title" class="team-title">{{ member.title }}</p>
					<p v-if="member.location" class="team-location">
						<i class="bi bi-geo-alt"></i> {{ member.location }}
					</p>
					<button class="btn btn-outline-primary contact-btn" @click="openModal(member)">
						More Info
					</button>
				</div>
			</div>
		</div>

		<TeamModal :member="selectedMember" :isOpen="isModalOpen" @close="closeModal" />
	</div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import TeamModal from './TeamModal.vue';

export default {
	name: 'TeamFilter',
	components: { TeamModal },
	setup() {
		// State
		const team = ref([]);
		const locations = ref([]);
		const departments = ref([]);
		const selectedLocation = ref('');
		const selectedDepartment = ref('');
		const searchQuery = ref('');
		const loading = ref(false);
		const error = ref(null);
		const selectedMember = ref(null);
		const isModalOpen = ref(false);
		let searchDebounce = null;

		// Computed - Client-side filtering
		const filteredTeam = computed(() => {
			let result = team.value;

			// Filter by location
			if (selectedLocation.value) {
				result = result.filter(
					member => member.location_id === parseInt(selectedLocation.value)
				);
			}

			// Filter by department
			if (selectedDepartment.value) {
				result = result.filter(member =>
					member.departments.includes(selectedDepartment.value)
				);
			}

			// Filter by search query
			if (searchQuery.value.trim()) {
				const query = searchQuery.value.toLowerCase().trim();
				result = result.filter(member => member.name.toLowerCase().includes(query));
			}

			return result;
		});

		// Methods
		const fetchTeamData = async () => {
			loading.value = true;
			error.value = null;

			try {
				const params = new URLSearchParams({
					action: 'get_team_members',
				});

				const response = await fetch(`${window.jaeckleData.ajaxUrl}?${params}`);

				if (!response.ok) {
					throw new Error('Failed to fetch team data');
				}

				const data = await response.json();

				if (data.success) {
					team.value = data.data.team;
					locations.value = data.data.locations;
					departments.value = data.data.departments;
				} else {
					throw new Error(data.data?.message || 'Failed to load team');
				}
			} catch (err) {
				error.value = err.message;
				console.error('Error fetching team data:', err);
			} finally {
				loading.value = false;
			}
		};

		const debouncedSearch = () => {
			clearTimeout(searchDebounce);
			searchDebounce = setTimeout(() => {
				// Search is reactive via computed, no action needed
			}, 300);
		};

		const clearSearch = () => {
			searchQuery.value = '';
		};

		const openModal = member => {
			selectedMember.value = member;
			isModalOpen.value = true;
			document.body.style.overflow = 'hidden';
		};

		const closeModal = () => {
			isModalOpen.value = false;
			selectedMember.value = null;
			document.body.style.overflow = '';
		};

		const applyUrlFilters = () => {
			const params = new URLSearchParams(window.location.search);

			const searchParam = params.get('search') || params.get('q');
			if (searchParam) {
				searchQuery.value = searchParam;
			}

			const locationParam = params.get('location') || params.get('location_id');
			if (locationParam) {
				const matchedById = locations.value.find(
					location => String(location.id) === String(locationParam)
				);

				if (matchedById) {
					selectedLocation.value = String(matchedById.id);
				} else {
					const normalizedLocation = locationParam.toLowerCase().trim();
					const matchedByTitle = locations.value.find(
						location => location.title.toLowerCase() === normalizedLocation
					);

					if (matchedByTitle) {
						selectedLocation.value = String(matchedByTitle.id);
					}
				}
			}

			const departmentParam = params.get('department');
			if (departmentParam) {
				const normalizedDepartment = departmentParam.toLowerCase().trim();
				const matchedDepartment = departments.value.find(
					department =>
						department.slug.toLowerCase() === normalizedDepartment ||
						department.name.toLowerCase() === normalizedDepartment
				);

				if (matchedDepartment) {
					selectedDepartment.value = matchedDepartment.slug;
				}
			}
		};

		// Lifecycle
		onMounted(() => {
			fetchTeamData().then(() => {
				applyUrlFilters();
			});
		});

		return {
			team,
			locations,
			departments,
			selectedLocation,
			selectedDepartment,
			searchQuery,
			loading,
			error,
			filteredTeam,
			debouncedSearch,
			clearSearch,
			selectedMember,
			isModalOpen,
			openModal,
			closeModal,
		};
	},
};
</script>

<style scoped>
/* Component-specific styles - main styles in _team-filter.scss */
</style>
